import React, {
    forwardRef,
    MutableRefObject,
    useState,
    ReactNode
} from 'react';
import styled, { css } from 'styled-components';
import { ImageFit, TSize, CSSPixelValue } from '../../tokens/types';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import HBText, { TTextType } from '../HBText/HBText';
import HBModalBase, { ModalControlProps } from '../HBModalBase/HBModalBase';
import HBModalCloseButton from '../HBModalBase/HBModalCloseButton';
import type {
    CalloutContentProps,
    CalloutLayout
} from '../common/Callout/Callout.types';
import PaginationDots from '../common/PaginationDots';
import PreloadMediaElements from '../common/PreloadMediaElements';
import CalloutMediaElement from '../common/Callout/CalloutMediaElement';
import { getTestIdProps, isLottieAnimation } from '../HBBanner/utils';
import { useWindowSize } from '../../hooks/useWindowSize';
import { BasicActionButtonsProps } from '../common/ActionButtons';
import { mediumUp } from '../../styles/breakpoints';
import HBModalActions from '../HBModalBase/HBModalActions';
import { IMediaElementProps } from '../HBMediaElement/HBMediaElement';
import HBDrawer from '../HBDrawer/HBDrawer';

export type CalloutModalProps = Props &
    ModalControlProps &
    BasicActionButtonsProps &
    CalloutContentProps;

interface Props
    extends Pick<IMediaElementProps, 'autoPlay' | 'muted' | 'controls'> {
    /** Controls title and description text sizes; ignored on mobile where 'md' is always used. */
    size?: Extract<TSize, 'sm' | 'md' | 'lg'>;
    /** Controls content arrangement: 'default' stacks media above text, 'side-by-side' places media beside text, 'center' centers text alignment; forced to 'default' on mobile. */
    layout: CalloutLayout;
    /** Maximum pixel width of the modal container on desktop. */
    maxWidth: number;
    /** Controls how the media element fills its container (e.g. 'cover', 'contain'). */
    imageFit: ImageFit;
    /** Fixed pixel width for the media element. */
    imageWidth: number;
    /** Adds padding around the media element; only applies on desktop. */
    withImagePadding?: boolean;
    /** Theme for the close button icon color: 'light' for dark backgrounds, 'dark' for light backgrounds. */
    closeButtonTheme?: 'light' | 'dark';
    /** Small tertiary text, typically for disclaimers or plan info. On desktop it renders below the action buttons; on mobile it renders at the end of the drawer content, above the drawer's pinned action buttons. */
    belowCTAText?: ReactNode;
}

const HBCalloutModal = (
    {
        maxWidth,
        withImagePadding,
        imageWidth,
        imageFit,
        size: _size = 'md',
        layout: _layout,
        testId,
        testIdName,
        closeButtonTheme,
        id,
        isOpen,
        onClose,
        belowCTAText,
        autoPlay,
        muted,
        controls,
        ...props
    }: CalloutModalProps & MustIncludeOneOfBasicTestIdWithIdProps,
    ref: MutableRefObject<HTMLDivElement>
) => {
    const [currStepIndex, setCurrStepIndex] = useState(0);
    const { isMediumUp } = useWindowSize();

    const layout: CalloutLayout = isMediumUp ? _layout : 'default';
    const size: Props['size'] = isMediumUp ? _size : 'md';

    const isSideBySide = layout === 'side-by-side';
    const isPaginated = 'steps' in props;
    const align = isSideBySide
        ? 'start'
        : layout === 'center'
        ? 'center'
        : 'end';

    const { titleType, descriptionType } = SIZES[size];

    let title: ReactNode;
    let description: ReactNode;
    let mediaPath: string | object;
    let primaryAction: BasicActionButtonsProps['primaryAction'];
    let secondaryAction: BasicActionButtonsProps['secondaryAction'];

    if (isPaginated) {
        const step = props.steps[currStepIndex];
        title = step.title;
        description = step.description;
        mediaPath = step.mediaPath;
        primaryAction = {
            text: step.primaryActionText,
            onClick: () => {
                step.primaryActionOnClick?.();
                if (currStepIndex < props.steps.length - 1) {
                    setCurrStepIndex(prev => prev + 1);
                } else {
                    onClose();
                    setCurrStepIndex(0);
                }
            }
        };
        secondaryAction = 'secondaryActionText' in step && {
            text: step.secondaryActionText,
            onClick: () => setCurrStepIndex(prev => prev - 1)
        };
    } else {
        title = props.title;
        description = props.description;
        mediaPath = props.mediaPath;
        primaryAction = props.primaryAction;
        secondaryAction = props.secondaryAction;
    }

    const paginationContent = isPaginated && (
        <>
            <PaginationDots
                activeIndex={currStepIndex}
                count={props.steps.length}
            />
            {isOpen && (
                <PreloadMediaElements
                    mediaPaths={props.steps.map(step =>
                        isLottieAnimation(step.mediaPath)
                            ? undefined
                            : step.mediaPath
                    )}
                />
            )}
        </>
    );

    const mediaContent = (
        <CalloutMediaElement
            mediaPath={mediaPath}
            imageFit={imageFit}
            withImagePadding={isMediumUp && withImagePadding}
            layout={layout}
            width={imageWidth}
            autoPlay={autoPlay}
            muted={muted}
            controls={controls}
        />
    );

    const children = (
        <Container $layout={layout} $maxWidth={isMediumUp && maxWidth}>
            {!isSideBySide && mediaContent}
            <Content>
                <TextContainer $layout={layout} $size={size}>
                    {typeof title === 'string' ? (
                        <HBText type={titleType} testId="callout-modal-title">
                            {title}
                        </HBText>
                    ) : (
                        title
                    )}
                    {typeof description === 'string' ? (
                        <HBText
                            type={descriptionType}
                            testId="callout-modal-description"
                        >
                            {description}
                        </HBText>
                    ) : (
                        description
                    )}
                </TextContainer>
                <FooterWrapper $layout={layout}>
                    {
                        // Avoid of rendering an empty footer, it causes a gap between the content and the belowCTAText
                        (paginationContent || isMediumUp) && (
                            <Footer $layout={layout}>
                                {!isSideBySide && paginationContent}
                                {isMediumUp && (
                                    <HBModalActions
                                        primaryAction={primaryAction}
                                        secondaryAction={secondaryAction}
                                        align={align}
                                    />
                                )}
                                {isSideBySide && paginationContent}
                            </Footer>
                        )
                    }
                    {belowCTAText && (
                        <HBText type="body12" color="text.tertiary">
                            {belowCTAText}
                        </HBText>
                    )}
                </FooterWrapper>
            </Content>
            {isSideBySide && mediaContent}
        </Container>
    );

    if (!isMediumUp) {
        const primaryActionWithTestIds = primaryAction && {
            ...primaryAction,
            ...getTestIdProps({ testId, testIdName, suffix: 'primary' })
        };
        const secondaryActionWithTestIds = secondaryAction && {
            ...secondaryAction,
            ...getTestIdProps({ testId, testIdName, suffix: 'secondary' })
        };
        return (
            <HBDrawer
                open={isOpen}
                onClose={() => onClose()}
                primaryAction={primaryActionWithTestIds}
                secondaryAction={secondaryActionWithTestIds}
                removeTopPadding
            >
                {children}
            </HBDrawer>
        );
    }

    return (
        <HBModalBase
            id={id}
            ref={ref}
            isOpen={isOpen}
            testId={testId}
            testIdName={testIdName}
            onClose={onClose}
            closeOnOutsideClick={props.closeOnOutsideClick}
        >
            {children}
            <HBModalCloseButton theme={closeButtonTheme} />
        </HBModalBase>
    );
};

export default forwardRef(HBCalloutModal);

// -------------------------- Styles -------------------------- //

const SIZES: Record<
    CalloutModalProps['size'],
    {
        titleType: TTextType;
        descriptionType: TTextType;
        textContainerGap: CSSPixelValue;
    }
> = {
    sm: {
        titleType: 'title20',
        descriptionType: 'body14',
        textContainerGap: '12px'
    },
    md: {
        titleType: 'display24',
        descriptionType: 'body16',
        textContainerGap: '16px'
    },
    lg: {
        titleType: 'display32',
        descriptionType: 'body18',
        textContainerGap: '20px'
    }
};

type $Layout = { $layout: CalloutModalProps['layout'] };

const Container = styled.div<$Layout & { $maxWidth: number }>(
    ({ $layout, $maxWidth }) => css`
        display: flex;
        max-width: ${$maxWidth}px;
        overflow: hidden;
        border-radius: inherit;
        position: relative;
        ${$layout !== 'side-by-side' &&
        css`
            flex-direction: column;
        `}
    `
);

const Content = styled.div`
    padding: 32px 0 0;
    ${mediumUp} {
        padding: 32px;
    }
    gap: 32px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-shrink: 1000000;
`;

const TextContainer = styled.div<
    $Layout & { $size: CalloutModalProps['size'] }
>`
    display: flex;
    flex-direction: column;
    gap: ${({ $size }) => SIZES[$size].textContainerGap};
    ${({ $layout }) =>
        $layout === 'center' &&
        css`
            text-align: center;
        `}
    white-space: pre-line;
`;

const Footer = styled.div<$Layout>`
    display: flex;
    gap: 24px;
    ${({ $layout }) =>
        $layout === 'center'
            ? css`
                  flex-direction: column;
                  align-items: center;
              `
            : css``};
`;

const FooterWrapper = styled.div<$Layout>`
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${({ $layout }) =>
        $layout === 'center' &&
        css`
            align-items: center;
        `};
`;
